/**
 * Web Vitals & Network Diagnostics Service
 *
 * Simple shared service for:
 * - LCP (final value only)
 * - INP (only 'poor' ratings OR significant changes to reduce volume)
 * - CLS (only 'poor' ratings OR cumulative > 0.1 to reduce volume)
 * - Network (strength + device/browser info, only on issues)
 *
 * Navigation TTI tracking is handled by navigationTTI.ts
 *
 * Used by resourceMonitor.ts and promiseExecutorMonitoring.ts
 *
 * ## When Data is Reported:
 *
 * ### logResourcePerformance (webVital):
 * - LCP: Once per page (final value only, triggered by user interaction or visibility change)
 * - INP: Only when rating is 'poor' (>500ms) OR value increased by >100ms
 * - CLS: Only when rating is 'poor' (>0.25) OR cumulative > 0.1
 *
 * ### logPageLoadPerformance (networkDiagnostics):
 * - Initial: Only if network strength is 'fair', 'poor', or 'offline'
 * - Online/Offline: When connection status changes
 * - Connection Change: When effectiveType changes (e.g., 4g -> 3g)
 *
 * ## Estimated Data Volume:
 * - LCP: 1 request/page (~200 bytes)
 * - INP: 0-3 requests/page (~250 bytes each) - filtered to reduce noise
 * - CLS: 0-2 requests/page (~220 bytes each) - filtered to reduce noise
 * - Network: 0-3 requests/session (~280 bytes each) - only on issues
 * - Total: ~0.5-2 KB per page view (optimized from ~2-8 KB)
 */

import PerformanceMonitoringService from '@/services/PerformanceMonitoringService'
import store from '@/store'
import {
  onLCP,
  onINP,
  onCLS,
  type LCPMetricWithAttribution,
  type INPMetricWithAttribution,
  type CLSMetricWithAttribution,
} from 'web-vitals/attribution'
import { shouldReportWebVital, shouldReportNetworkDiagnostics, type WebVitalMetric } from './samplingService'
import { RouterRegistry } from './routerRegistry'
import { initNavigationTTI, startNavigationTTI, isNavigationTracking } from './navigationTTI'

// Re-export navigation TTI functions for backward compatibility with tests
// These functions are now in separate modules but were previously exported from here
export { startNavigationTTI as markSoftNavigationStart, isNavigationTracking }

// Deprecated: markSoftNavigationEnd is now a no-op (stability detection handles navigation end)
export function markSoftNavigationEnd(): void {
  console.log('[SoftNav] markSoftNavigationEnd called (no-op - using stability detection)')
}

// Deprecated: getLatestSoftNav - TTI data is now reported directly via new modules
export function getLatestSoftNav(): null {
  console.warn('[SoftNav] getLatestSoftNav is deprecated - TTI data is reported directly via navigationTTI/interactionTTI modules')
  return null
}

// ============================================================================
// Types - Minimal Data Structures
// ============================================================================

/** LCP data - final value only */
export interface LCPData {
  lcp: number // LCP in ms
  rating: string // 'good' | 'needs-improvement' | 'poor'
  routeName: string
  element?: string // CSS selector of LCP element
  timestamp: number
}

/** INP data - with root cause for debugging */
export interface INPData {
  inp: number // INP in ms
  rating: string
  // Root cause - WHERE it happened
  routeName: string
  eventTarget: string // e.g., 'button.save-btn'
  eventType: string // 'pointer' | 'keyboard'
  // Root cause - WHY (timing breakdown)
  inputDelay?: number // main thread was busy
  processingDuration?: number // event handler time
  presentationDelay?: number // render time
  timestamp: number
}

/** CLS data - with root cause for debugging */
export interface CLSData {
  cls: number // CLS score (this shift)
  cumulativeValue: number // Total CLS
  rating: string
  // Root cause - WHERE it happened
  routeName: string
  // Root cause - WHAT caused it
  shiftSourceNode?: string // e.g., 'img.hero'
  largestShiftTarget?: string // element that shifted
  hadRecentInput?: boolean // true = user-initiated (expected)
  timestamp: number
}

/** Network data - with device/browser info */
export interface NetworkData {
  // Connection quality
  strength: 'excellent' | 'good' | 'fair' | 'poor' | 'offline'
  downloadMbps?: number // download speed
  rttMs?: number // latency
  effectiveType?: string // '4g' | '3g' | '2g' | 'slow-2g'
  connectionType?: string // 'wifi' | 'cellular' | 'ethernet'
  // Device info
  deviceType: 'desktop' | 'mobile' | 'tablet'
  os: string // 'Windows' | 'macOS' | 'iOS' | 'Android' | 'Linux'
  browser: string // 'Chrome' | 'Firefox' | 'Safari' | 'Edge'
  browserVersion: string
  // Context
  routeName: string
}

// ============================================================================
// Helpers
// ============================================================================

const getSessionId = (): string | undefined => {
  try {
    return sessionStorage.getItem('sessionId') || undefined
  } catch {
    return undefined
  }
}

const getUserId = (): string | undefined => {
  try {
    return store.state.user?.user?.id
  } catch {
    return undefined
  }
}

const getLocationId = (): string | null => {
  try {
    const match = window.location.pathname.match(/\/location\/([^/]+)/)
    return match ? match[1] : null
  } catch {
    return null
  }
}

const getRouteName = (): string => {
  try {
    // Try RouterRegistry first (the app's central router registry)
    const router = RouterRegistry.getRouter()
    if (router?.currentRoute?.value?.name) {
      return router.currentRoute.value.name as string
    }
    return (window as any).__VUE_ROUTER__?.currentRoute?.value?.name || 'unknown'
  } catch {
    return 'unknown'
  }
}

/** Detect device type from user agent */
const getDeviceType = (): 'desktop' | 'mobile' | 'tablet' => {
  const ua = navigator.userAgent.toLowerCase()
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet'
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile'
  return 'desktop'
}

/** Detect OS from user agent */
const getOS = (): string => {
  const ua = navigator.userAgent
  if (/Windows/i.test(ua)) return 'Windows'
  if (/Mac OS X|macOS/i.test(ua)) return 'macOS'
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS'
  if (/Android/i.test(ua)) return 'Android'
  if (/Linux/i.test(ua)) return 'Linux'
  return 'Unknown'
}

/** Detect browser and version */
const getBrowserInfo = (): { browser: string; version: string } => {
  const ua = navigator.userAgent
  let browser = 'Unknown'
  let version = ''

  if (/Edg\//i.test(ua)) {
    browser = 'Edge'
    version = ua.match(/Edg\/(\d+)/)?.[1] || ''
  } else if (/Chrome/i.test(ua)) {
    browser = 'Chrome'
    version = ua.match(/Chrome\/(\d+)/)?.[1] || ''
  } else if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) {
    browser = 'Safari'
    version = ua.match(/Version\/(\d+)/)?.[1] || ''
  } else if (/Firefox/i.test(ua)) {
    browser = 'Firefox'
    version = ua.match(/Firefox\/(\d+)/)?.[1] || ''
  }

  return { browser, version }
}

/** Get element identifier (minimal) */
const getElementId = (node: Node | null | undefined): string | undefined => {
  if (!node) return undefined
  const el = node as Element
  const tag = el.tagName?.toLowerCase() || ''
  const id = el.id ? `#${el.id}` : ''
  const cls = el.className ? `.${el.className.toString().split(' ')[0]}` : ''
  return `${tag}${id}${cls}` || undefined
}

// ============================================================================
// Latest Values (for external access)
// ============================================================================

let latestLCP: LCPData | null = null
let latestINP: INPData | null = null
let latestCLS: CLSData | null = null
let latestNetwork: NetworkData | null = null

export const getLatestLCP = (): LCPData | null => latestLCP
export const getLatestINP = (): INPData | null => latestINP
export const getLatestCLS = (): CLSData | null => latestCLS
export const getLatestNetwork = (): NetworkData | null => latestNetwork

// ============================================================================
// Send Web Vitals (individual events, no batching)
// ============================================================================

interface WebVitalPayload {
  type: 'LCP' | 'INP' | 'CLS'
  sessionId?: string
  userId?: string
  locationId: string | null
  url: string
  timestamp: string
  [key: string]: any
}

/** Send a single web vital event immediately */
const sendWebVital = (payload: WebVitalPayload) => {
  PerformanceMonitoringService.logPageLoadPerformance(payload)
}

/** No-op for backwards compatibility (batching removed) */
export const flushWebVitals = () => {
  // No batching - events are sent immediately
}

// ============================================================================
// LCP Monitoring (final value only)
// ============================================================================

let lcpInitialized = false

export function initLCPMonitoring(): void {
  if (typeof window === 'undefined' || lcpInitialized) return
  lcpInitialized = true

  try {
    onLCP(
      (metric: LCPMetricWithAttribution) => {
        const data: LCPData = {
          lcp: Math.round(metric.value),
          rating: metric.rating,
          routeName: getRouteName(),
          element: metric.attribution?.target,
          timestamp: Date.now(),
        }

        latestLCP = data

        // Unified session-based sampling (LCP: 100%, always reports 'poor' ratings)
        if (!shouldReportWebVital('LCP', metric.rating)) {
          return
        }

        // With reportAllChanges: false, this callback fires only once
        // with the final LCP value (on user interaction or visibility change)
        sendWebVital({
          type: 'LCP',
          sessionId: getSessionId(),
          userId: getUserId(),
          locationId: getLocationId(),
          url: window.location.href,
          ...data,
          timestamp: new Date().toISOString(),
        })
      },
      { reportAllChanges: false } // Only get final LCP value (no debouncing needed)
    )
  } catch {
    // Silently fail
  }
}

// ============================================================================
// INP Monitoring (filtered: only 'poor' ratings OR significant changes)
// ============================================================================

let inpInitialized = false
let lastReportedINPValue = 0

export function initINPMonitoring(): void {
  if (typeof window === 'undefined' || inpInitialized) return
  inpInitialized = true

  try {
    onINP(
      (metric: INPMetricWithAttribution) => {
        const { attribution } = metric
        const value = Math.round(metric.value)
        const data: INPData = {
          inp: value,
          rating: metric.rating,
          routeName: getRouteName(),
          eventTarget: attribution?.interactionTarget || 'unknown',
          eventType: attribution?.interactionType || 'pointer',
          ...(attribution?.inputDelay &&
            attribution.inputDelay > 0 && {
              inputDelay: Math.round(attribution.inputDelay),
            }),
          ...(attribution?.processingDuration &&
            attribution.processingDuration > 0 && {
              processingDuration: Math.round(attribution.processingDuration),
            }),
          ...(attribution?.presentationDelay &&
            attribution.presentationDelay > 0 && {
              presentationDelay: Math.round(attribution.presentationDelay),
            }),
          timestamp: Date.now(),
        }

        latestINP = data

        // Unified session-based sampling (INP: 30%, always reports 'poor' ratings)
        if (!shouldReportWebVital('INP', metric.rating)) {
          return
        }

        // Additional filtering: only report if value increased significantly (>100ms)
        // This reduces noise from minor INP changes

        lastReportedINPValue = value

        sendWebVital({
          type: 'INP',
          sessionId: getSessionId(),
          userId: getUserId(),
          locationId: getLocationId(),
          url: window.location.href,
          ...data,
          timestamp: new Date().toISOString(),
        })
      },
      { reportAllChanges: true }
    )
  } catch {
    // Silently fail
  }
}

// ============================================================================
// CLS Monitoring (filtered: only 'poor' ratings OR cumulative > threshold)
// ============================================================================

let clsInitialized = false
let lastReportedCLSCumulative = 0

export function initCLSMonitoring(): void {
  if (typeof window === 'undefined' || clsInitialized) return
  clsInitialized = true

  try {
    onCLS(
      (metric: CLSMetricWithAttribution) => {
        const { attribution } = metric
        const shiftSource = attribution?.largestShiftSource
        const shiftEntry = attribution?.largestShiftEntry
        const cumulativeValue = Math.round(metric.value * 1000) / 1000

        const data: CLSData = {
          cls: Math.round(metric.delta * 1000) / 1000,
          cumulativeValue,
          rating: metric.rating,
          routeName: getRouteName(),
          shiftSourceNode: getElementId(shiftSource?.node),
          largestShiftTarget: attribution?.largestShiftTarget,
          hadRecentInput: shiftEntry?.hadRecentInput,
          timestamp: Date.now(),
        }

        latestCLS = data

        // Skip user-initiated shifts (not a problem)
        const isUserInitiated = shiftEntry?.hadRecentInput === true
        if (isUserInitiated) {
          return
        }

        // Unified session-based sampling (CLS: 20%, always reports 'poor' ratings)
        if (!shouldReportWebVital('CLS', metric.rating)) {
          return
        }

        // Additional filtering: report if cumulative exceeds threshold AND increased

        lastReportedCLSCumulative = cumulativeValue

        sendWebVital({
          type: 'CLS',
          sessionId: getSessionId(),
          userId: getUserId(),
          locationId: getLocationId(),
          url: window.location.href,
          ...data,
          timestamp: new Date().toISOString(),
        })
      },
      { reportAllChanges: true }
    )
  } catch {
    // Silently fail
  }
}

// ============================================================================
// Network Monitoring (with device/browser info)
// ============================================================================

let networkInitialized = false

/** Calculate connectivity strength */
const getConnectivityStrength = (): 'excellent' | 'good' | 'fair' | 'poor' | 'offline' => {
  if (!navigator.onLine) return 'offline'

  const nav = navigator as any
  const conn = nav.connection || nav.mozConnection || nav.webkitConnection
  if (!conn) return 'good'

  const { effectiveType, rtt, downlink } = conn

  if (effectiveType === 'slow-2g' || effectiveType === '2g') return 'poor'
  if (effectiveType === '3g') return 'fair'
  if (effectiveType === '4g') {
    if (rtt > 500 || downlink < 0.5) return 'poor'
    if (rtt > 300 || downlink < 1) return 'fair'
    if (rtt < 100 && downlink > 5) return 'excellent'
  }

  return 'good'
}

/** Collect network data */
export const getNetworkData = (): NetworkData => {
  const nav = navigator as any
  const conn = nav.connection || nav.mozConnection || nav.webkitConnection
  const { browser, version } = getBrowserInfo()

  return {
    strength: getConnectivityStrength(),
    downloadMbps: conn?.downlink,
    rttMs: conn?.rtt,
    effectiveType: conn?.effectiveType,
    connectionType: conn?.type,
    deviceType: getDeviceType(),
    os: getOS(),
    browser,
    browserVersion: version,
    routeName: getRouteName(),
  }
}

/** Send network diagnostics to server (respects unified session sampling) */
const sendNetworkDiagnostics = () => {
  // Unified session-based sampling
  if (!shouldReportNetworkDiagnostics()) {
    return
  }

  const network = getNetworkData()
  PerformanceMonitoringService.logPageLoadPerformance({
    type: 'NetworkDiagnostics',
    sessionId: getSessionId(),
    userId: getUserId(),
    locationId: getLocationId(),
    url: window.location.href,
    ...network,
    timestamp: new Date().toISOString(),
  })
}

export function initNetworkMonitoring(): void {
  if (typeof window === 'undefined' || networkInitialized) return
  networkInitialized = true

  try {
    latestNetwork = getNetworkData()

    // Send network diagnostics once, 10 seconds after page load
    setTimeout(() => {
      sendNetworkDiagnostics()
    }, 10000)

    // Update latestNetwork on connection changes (for accurate snapshots)
    const nav = navigator as any
    const conn = nav.connection || nav.mozConnection || nav.webkitConnection
    if (conn) {
      conn.addEventListener('change', () => {
        latestNetwork = getNetworkData()
      })
    }

    // Update on online/offline
    window.addEventListener('online', () => {
      latestNetwork = getNetworkData()
    })
    window.addEventListener('offline', () => {
      latestNetwork = getNetworkData()
    })
  } catch {
    // Silently fail
  }
}

// ============================================================================
// Initialize All
// ============================================================================

export function initWebVitalsService(): void {
  // Core Web Vitals
  initLCPMonitoring()
  initINPMonitoring()
  initCLSMonitoring()
  initNetworkMonitoring()

  // Navigation TTI tracking
  initNavigationTTI()

  // Flush web vitals batch on page unload - global lifecycle listener
  if (typeof window !== 'undefined') {
    // eslint-disable-next-line listeners/no-missing-remove-event-listener, listeners/no-inline-function-event-listener
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flushWebVitals()
      }
    })
  }
}
